import { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useParams, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import './styles/themes.css';
import './styles/animations.css';

import SecurityProvider from './components/security/SecurityProvider';
import { useSimpleGame } from './contexts/SimpleGameContext';
import { SwipeGameProvider } from './contexts/SwipeGameContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FontSizeProvider } from './contexts/FontSizeContext';
import { getTodaysPuzzle, getPuzzleByDate, isValidDateFormat, type DailyPuzzle } from './services/puzzleService';

// Lazy load heavy components to reduce initial bundle size
const GameScreen = lazy(() => import('./components/game/GameScreenSwipe'));
const SimpleAdminDashboard = lazy(() => import('./components/admin/SimpleAdminDashboard'));
const ShareButton = lazy(() => import('./components/sharing/ShareButton'));
const ShareModal = lazy(() => import('./components/sharing/ShareModal'));

type AppView = 'start' | 'game' | 'victory' | 'admin' | 'mfa-setup' | 'mfa-verify';

interface GameStats {
  steps: number;
  timeElapsed: number;
  path: string[];
  backButtonUsed: boolean;
}

/**
 * Error Page Component
 */
function PuzzleErrorPage(): JSX.Element {
  const navigate = useNavigate();

  return (
    <div
      className="min-h-dvh flex flex-col items-center justify-center"
      style={{
        backgroundColor: "#004289",
        fontFamily: "League Spartan, sans-serif",
        padding: "2rem"
      }}
    >
      {/* Header with Logo */}
      <div className="text-center mb-8">
        <img
          src="/images/BlueLinks Logotype.svg"
          alt="Blue Links"
          style={{
            width: '400px',
            height: 'auto',
            display: 'block',
            margin: '0 auto 1rem auto'
          }}
        />
        <div style={{ color: "#F9F7F1", fontSize: "14px" }}>
          Puzzle not available
        </div>
      </div>

      {/* Action Button */}
      <button
        onClick={() => {
          navigate('/');
          window.location.reload();
        }}
        className="px-8 py-3 rounded font-semibold transition-all duration-200"
        style={{
          backgroundColor: "#0084FF",
          color: "white",
          border: "none",
          fontSize: "18px",
          borderRadius: "6px"
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "#0070DD";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "#0084FF";
        }}
      >
        Go to Today's Puzzle
      </button>
    </div>
  );
}

/**
 * Game Route Component
 * Handles fetching and displaying puzzles for specific dates
 */
function GameRoute() {
  const { date } = useParams<{ date?: string }>();
  const [puzzle, setPuzzle] = useState<DailyPuzzle | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentView, setCurrentView] = useState<AppView>('game');
  const [gameStats, setGameStats] = useState<GameStats | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const { gameState, dispatch } = useSimpleGame();
  const navigate = useNavigate();

  // Fetch puzzle on mount or when date changes
  useEffect(() => {
    async function fetchPuzzle() {
      setLoading(true);
      setError(null);

      try {
        let puzzleData;

        if (date) {
          // Validate date format
          if (!isValidDateFormat(date)) {
            setError('Invalid date format. Please use YYYY-MM-DD format.');
            setLoading(false);
            return;
          }

          // Fetch puzzle for specific date
          const response = await getPuzzleByDate(date);
          puzzleData = response.puzzle;
        } else {
          // Fetch today's puzzle - if it doesn't exist, that's okay, just continue to game
          try {
            const response = await getTodaysPuzzle();
            puzzleData = response.puzzle;
          } catch (err) {
            // No puzzle for today - continue to game without puzzle data
            setLoading(false);
            return;
          }
        }

        setPuzzle(puzzleData);

        // Set start and goal articles in game context
        dispatch({
          type: 'START_TEST_GAME',
          payload: {
            startArticle: puzzleData.start.title,
            goalArticle: puzzleData.goal.title
          }
        });

      } catch (err) {
        console.error('Error fetching puzzle:', err);
        if (err instanceof Error) {
          setError(err.message);
        } else {
          setError('Failed to load puzzle');
        }
      } finally {
        setLoading(false);
      }
    }

    fetchPuzzle();
  }, [date, dispatch]);

  // Check for admin route on load and handle hash changes
  useEffect(() => {
    const checkRoute = () => {
      const hash = window.location.hash;
      if (hash === '#/admin') {
        setCurrentView('admin');
      } else if (hash === '#/mfa-setup') {
        setCurrentView('mfa-setup');
      } else if (hash === '#/mfa-verify') {
        setCurrentView('mfa-verify');
      } else {
        setCurrentView('game');
      }
    };

    // Check route on initial load
    checkRoute();

    // Listen for hash changes
    window.addEventListener('hashchange', checkRoute);

    // Cleanup
    return () => {
      window.removeEventListener('hashchange', checkRoute);
    };
  }, []);

  const handleGameWin = (stats: GameStats) => {
    setGameStats(stats);
    setCurrentView('victory');
  };

  // Show loading state
  if (loading) {
    return <div className="loading-screen">Loading puzzle...</div>;
  }

  // Show error state
  if (error) {
    return <PuzzleErrorPage />;
  }

  // Show appropriate view
  const renderView = () => {
    switch (currentView) {
      case 'game':
        return (
          <Suspense fallback={<div className="loading-screen">Loading game...</div>}>
            <GameScreen
              onBackToStart={() => navigate('/')}
              onGameWin={handleGameWin}
            />
          </Suspense>
        );
      case 'victory':
        return (
          <div className="victory-view">
            <div className="victory-content">
              <div className="victory-header">
                <h1 className="victory-title">🎉 Puzzle Complete!</h1>
                <p className="victory-subtitle">You found the path from {gameState.startArticle} to {gameState.goalArticle}!</p>
                {puzzle && (
                  <p className="puzzle-info">Puzzle #{puzzle.number} - {puzzle.date}</p>
                )}
              </div>

              {gameStats && (
                <div className="victory-stats">
                  <div className="stats-grid">
                    <div className="stat-card">
                      <div className="stat-value">{gameStats.steps}</div>
                      <div className="stat-label">Steps</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{Math.floor(gameStats.timeElapsed / 1000)}s</div>
                      <div className="stat-label">Time</div>
                    </div>
                    <div className="stat-card">
                      <div className="stat-value">{gameStats.backButtonUsed ? 'Yes' : 'No'}</div>
                      <div className="stat-label">Back Used</div>
                    </div>
                  </div>

                  <div className="path-display">
                    <h3>Your Path:</h3>
                    <div className="path-chain">
                      {gameStats.path.map((article, index) => (
                        <span key={index} className="path-step">
                          {article}
                          {index < gameStats.path.length - 1 && <span className="path-arrow">→</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className="victory-actions">
                <div className="sharing-section">
                  <h3>Share Your Achievement</h3>
                  <div className="share-buttons">
                    {gameStats && (
                      <Suspense fallback={<div>Loading share...</div>}>
                        <ShareButton
                          gameSession={{
                            startTime: Date.now() - gameStats.timeElapsed,
                            endTime: Date.now(),
                            startPage: {
                              title: gameState.startArticle,
                              id: 1,
                              extract: '',
                              links: [],
                              categories: [],
                              lastModified: new Date().toISOString()
                            },
                            targetPage: {
                              title: gameState.goalArticle,
                              id: 2,
                              extract: '',
                              links: [],
                              categories: [],
                              lastModified: new Date().toISOString()
                            },
                            path: gameStats.path,
                            currentPage: {
                              title: gameStats.path[gameStats.path.length - 1] || gameState.goalArticle,
                              id: 3,
                              extract: '',
                              links: [],
                              categories: [],
                              lastModified: new Date().toISOString()
                            },
                            difficulty: 'medium' as const,
                            id: 'victory-session',
                            userId: 'user-1',
                            clicks: gameStats.steps,
                            status: 'completed' as const,
                            hints: [],
                            achievements: []
                          }}
                          format="short"
                          variant="primary"
                          size="large"
                          className="primary-share-btn"
                        />
                      </Suspense>
                    )}

                    <button
                      onClick={() => setShowShareModal(true)}
                      className="share-options-btn"
                    >
                      More Options
                    </button>
                  </div>
                </div>

                <div className="navigation-section">
                  <button
                    onClick={() => navigate('/')}
                    className="play-again-btn"
                  >
                    🎮 Play Again
                  </button>

                  <button
                    onClick={() => window.location.hash = '#/admin'}
                    className="admin-btn"
                  >
                    📊 View Stats
                  </button>
                </div>
              </div>
            </div>

            {/* Share Modal */}
            {showShareModal && gameStats && (
              <Suspense fallback={<div>Loading share modal...</div>}>
                <ShareModal
                  isOpen={showShareModal}
                  onClose={() => setShowShareModal(false)}
                  gameSession={{
                    startTime: Date.now() - gameStats.timeElapsed,
                    endTime: Date.now(),
                    startPage: {
                      title: gameState.startArticle,
                      id: 1,
                      extract: '',
                      links: [],
                      categories: [],
                      lastModified: new Date().toISOString()
                    },
                    targetPage: {
                      title: gameState.goalArticle,
                      id: 2,
                      extract: '',
                      links: [],
                      categories: [],
                      lastModified: new Date().toISOString()
                    },
                    path: gameStats.path,
                    currentPage: {
                      title: gameStats.path[gameStats.path.length - 1] || gameState.goalArticle,
                      id: 3,
                      extract: '',
                      links: [],
                      categories: [],
                      lastModified: new Date().toISOString()
                    },
                    difficulty: 'medium' as const,
                    id: 'victory-session-modal',
                    userId: 'user-1',
                    clicks: gameStats.steps,
                    status: 'completed' as const,
                    hints: [],
                    achievements: []
                  }}
                  onShare={(success) => {
                    if (success) {
                      setShowShareModal(false);
                    }
                  }}
                />
              </Suspense>
            )}
          </div>
        );
      case 'admin':
        return (
          <Suspense fallback={<div className="loading-screen">Loading admin dashboard...</div>}>
            <SimpleAdminDashboard />
          </Suspense>
        );
      case 'mfa-setup':
        return (
          <div className="mfa-setup-view">
            <h1>MFA Setup</h1>
            <p>MFA setup interface will be implemented here</p>
            <button onClick={() => navigate('/')}>Back to Start</button>
          </div>
        );
      case 'mfa-verify':
        return (
          <div className="mfa-verify-view">
            <h1>MFA Verify</h1>
            <p>MFA verification interface will be implemented here</p>
            <button onClick={() => navigate('/')}>Back to Start</button>
          </div>
        );
      default:
        return (
          <Suspense fallback={<div className="loading-screen">Loading game...</div>}>
            <GameScreen
              onBackToStart={() => navigate('/')}
              onGameWin={handleGameWin}
            />
          </Suspense>
        );
    }
  };

  return renderView();
}

function AppContent() {
  return (
    <ThemeProvider>
      <FontSizeProvider>
        <SecurityProvider>
          <SwipeGameProvider>
            <BrowserRouter>
              <Routes>
                {/* Today's puzzle */}
                <Route path="/" element={<GameRoute />} />

                {/* Specific date puzzle */}
                <Route path="/:date" element={<GameRoute />} />

                {/* Catch all - redirect to today */}
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </BrowserRouter>
          </SwipeGameProvider>
        </SecurityProvider>
      </FontSizeProvider>
    </ThemeProvider>
  );
}

function App() {
  return <AppContent />;
}

export default App;
